import type { storageUsage } from "../db/schema";
import type { FailingWorkflowRow } from "./failingWorkflows";
import type { MonitoringRouteSummary } from "./monitoringRouteAuth";
import type { DependencySummaryRow } from "./monitoringWorkflowDependencies";

type MonitoringWorkflowRoute = {
  route: string;
  stepDir: string;
  routeType: string | null;
  routeAuth: string;
};

type MonitoringWorkflowDependency = {
  name: string;
  version: string | null;
  source: string;
};

// `lastRunAt` is null exactly when the window holds no rollup row for the
// workflow.
export type MonitoringWorkflowRow = {
  id: string;
  name: string;
  spaceId: string;
  badgeSeed: string | null;
  pausedAt: number | null;
  active: boolean;
  executionCount: number;
  lastRunAt: number | null;
};

export const STORAGE_USAGE_REFRESH_INTERVAL_MS = 3 * 60_000;

type MonitoringStorageRow = {
  spaceId: string;
  bytesUsed: number;
};

export type MonitoringStorageCategory =
  (typeof storageUsage.$inferSelect)["category"];

export type MonitoringStorageUsageRow = {
  spaceId: string;
  category: MonitoringStorageCategory;
  workflowId: string | null;
  workflowName: string | null;
  branchId: string | null;
  branchName: string | null;
  volumeId: string | null;
  volumeName: string | null;
  liveBytes: number;
};

// Withheld unless the viewer reads every space: the egress leg is tenant-wide,
// so a partial view would measure a different population than the other legs.
export type MonitoringTinesUnits = {
  compute: number;
  storage: number;
  egress: number;
  total: number;
};

export type MonitoringUnitsSpaceRow = {
  spaceId: string;
  compute: number;
  egress: number;
  storage: number;
  total: number;
  workflowCount: number;
  forecast?: number | null;
};

export type MonitoringUnitsWorkflowRow = {
  workflowId: string;
  spaceId: string;
  compute: number;
  egress: number;
  total: number;
  forecast?: number | null;
};

export type MonitoringUnitsTrendPoint = {
  periodStart: number;
  compute: number;
  storage: number;
  egress: number;
  total: number;
  spaces: { spaceId: string | null; total: number }[];
};

// The month-by-month units history, plus the run-rate projection for the open
// month. `months` is sparse — only months the ledger has rows for — since a
// tenant that ran nothing in a month has no rows. Withheld with the rest of the
// units block from a viewer who can't read every space.
export type MonitoringUnitsTrend = {
  months: MonitoringUnitsTrendPoint[];
  currentPeriodStart: number;
  projectedTotal: number | null;
};

export type MonitoringWorkflowsResponse = {
  workflows: MonitoringWorkflowRow[];
  failing: FailingWorkflowRow[];
  failureTrend: number[];
  storage: MonitoringStorageRow[];
  // Null unless the storage-usage-view flag is on for the tenant.
  storageUsage: MonitoringStorageUsageRow[] | null;
  // Null without the flag, or before the tenant’s first sample this month.
  storageSampledAt: number | null;
  units: MonitoringTinesUnits | null;
  unitsBySpace: MonitoringUnitsSpaceRow[] | null;
  unitsByWorkflow: MonitoringUnitsWorkflowRow[] | null;
  unitsTrend: MonitoringUnitsTrend | null;
  routeSummary: MonitoringRouteSummary;
  dependencySummary: DependencySummaryRow[];
};

// A single month's space and workflow breakdown, fetched on demand when a past
// month is opened in the units drill-in. Both null when the viewer can't read
// every space, mirroring the units block of the main report.
export type MonitoringUnitsMonthResponse = {
  unitsBySpace: MonitoringUnitsSpaceRow[] | null;
  unitsByWorkflow: MonitoringUnitsWorkflowRow[] | null;
};

export type MonitoringWorkflowRoutesRow = {
  id: string;
  name: string;
  spaceId: string;
  badgeSeed: string | null;
  pausedAt: number | null;
  routes: MonitoringWorkflowRoute[];
};

export type MonitoringWorkflowRoutesResponse = {
  workflows: MonitoringWorkflowRoutesRow[];
};

export type MonitoringWorkflowDependenciesRow = {
  id: string;
  name: string;
  spaceId: string;
  badgeSeed: string | null;
  pausedAt: number | null;
  dependencies: MonitoringWorkflowDependency[];
};

export type MonitoringWorkflowDependenciesResponse = {
  workflows: MonitoringWorkflowDependenciesRow[];
};
